/**
 * pageContextRegistry.ts
 *
 * Maps application routes to page-level metadata consumed by:
 *   1. The app-level GlobalChatSystem (instance source + placeholder)
 *   2. The orchestrator system prompt (page summary for context-aware responses)
 *
 * This is the ONLY place per-page chat differences should live.
 * GlobalChatSystem behaviour is otherwise identical on every route.
 *
 * ⚠️ THIS FILE IS THE SIGNED-OUT PREST0N'S GROUND TRUTH. It is generated into
 * functions/src/guestSession/siteDigest.generated.ts by
 * scripts/generate-guest-site-digest.mjs, so a stale sentence here becomes
 * what a visitor is told. The /pricing summary used to restate the entire
 * commitment ladder as hand-typed prose; it now interpolates from
 * src/config/workUnits.ts, and the free internship's words from
 * src/config/internshipWords.ts (built from src/config/trialTerms.ts). The
 * generator supplies those same constants when it evaluates this literal, so
 * keep any new interpolation to the names it injects (see pricingScope() there).
 */

import { WU_PRICING, MONTHLY_WU_PER_PRESTON, STANDARD_RATE_USD_PER_WU } from './workUnits';
import { MAX_FILES_PER_REQUEST_UI } from './composerLimits';
import { INTERNSHIP_WORDS } from './internshipWords';
import { PRIMARY_OFFER_SEATS } from './priceLadder';
import { BURDENED_HIRE } from '../roi/burdenHeadline.generated';
// Prest0's own audit status, in the words config/compliance-status.json makes (the generator injects the same).
import { COMPLIANCE } from './complianceStatus.generated';

/**
 * Page-level context handed to the deciding model with a turn — what the
 * user is currently viewing. Built from this registry (usePageChatConfig).
 * Moved here from the deleted services/globalTools/orchestrator/prompts.ts
 * (D13-6b, 2026-08-25): the name predates the in-browser orchestrator's
 * removal and is kept because the live prest0n-session lane's wire contract
 * carries it.
 */
export interface OrchestratorPageContext {
  /** Route pathname, e.g. '/team' */
  pathname: string;
  /** Human-readable page name, e.g. 'Team' */
  pageName?: string;
  /** One-paragraph summary of the page */
  summary: string;
  /** Hint about the kinds of questions typical for this page */
  contextHint?: string;
}

export interface PageChatContext {
  /** Identifies this page to analytics / orchestrator routing */
  source: string;
  /** Short human-readable placeholder shown in the prompt input */
  placeholder?: string;
  /** One-paragraph summary injected into the orchestrator's system prompt */
  summary: string;
  /**
   * Optional hint describing what kind of questions users typically ask here.
   * Used to prime the orchestrator for better first-hop routing.
   */
  contextHint?: string;
  /**
   * When true, balance checking is suppressed. No route sets this today —
   * the last user (/justiceisurright's pre-Firebase page-password gate)
   * became a Firebase account gate in Round M (2026-07-15) and chat there is
   * now uniform with every other page.
   */
  skipBalanceCheck?: boolean;
  /**
   * Welcome message shown when a user opens a fresh chat session on this page.
   */
  welcomeMessage?: { text: string; html?: string };
  /**
   * One-click starter prompts under the welcome bubble on this page. Account
   * (tenant) starters still win once loaded — these are the per-page default
   * for visitors without tenant config (e.g. a signed-out invitee).
   */
  welcomeStarters?: string[];
  /**
   * When true, the Slack-style thread switcher (with a "+" button to start a
   * new thread/project) is exposed on this route. The switcher is still
   * AND-gated in GlobalChatSystem against the internal-Prest0n / prest0-vm
   * source check, so internal instances never render it even if this is set.
   *
   * Default: undefined (falls back to the GlobalChatSystem default, which is
   * currently off — see commit d7d1fd54). Account-specific surfaces that want
   * multi-thread/project switching should opt in here.
   */
  enableThreadSwitcher?: boolean;
}

/** Fallback used for any route not explicitly registered. */
export const DEFAULT_PAGE_CONTEXT: PageChatContext = {
  source: 'site',
  placeholder: 'ask prest0 anything…',
  summary:
    'The user is on the prest0.ai website. Prest0 is an AI-powered platform that ' +
    'helps businesses create, deploy, and monetize personalised AI agents at scale.',
  contextHint: 'General product questions, onboarding, pricing, or capabilities.',
};

/**
 * Route-to-context map.
 * Keys are exact pathnames.  Wildcard matching is handled in usePageChatConfig.
 */
export const PAGE_CONTEXT_REGISTRY: Record<string, PageChatContext> = {
  // ── Home ────────────────────────────────────────────────────────────────
  // Rewritten 2026-09-09 with the page. The previous summary described "a
  // platform for creating scalable, personalised AI agents that disrupt
  // industries" and "the founder's vision" — generic, and nothing the page has
  // said for months. This is what the signed-out Prest0n is told about the page
  // it is sitting on, so a stale summary here is a model describing a different
  // website. The concrete numbers interpolate from the SSOT rather than being
  // typed, because the scraped page source cannot carry them: the JSX puts them
  // inside `{…}` and <strong>, both of which end a text node for the extractor.
  '/': {
    source: 'home',
    placeholder: 'ask prest0 anything…',
    // Rewritten 2026-09-09 with the page. The previous summary described "a
    // platform for creating scalable, personalised AI agents that disrupt
    // industries" and "the founder's vision" — generic, and nothing the page
    // has said for months. This is what the signed-out Prest0n is told about
    // the page it is sitting on.
    //
    // DELIBERATELY SHORT, and not a retelling of the page. The scraped page
    // index (knowledgeSource.generated.json) already carries the home copy
    // verbatim, so this covers only the two things it CANNOT: the file cap,
    // which the JSX holds inside `{…}` and <strong> (both end a text node for
    // the extractor), and the two-lane integration rule, whose second half is
    // the one thing a model must not get wrong here. The digest cap is 2,500
    // characters across every route and /pricing sits behind this one — a long
    // summary here silently costs the model the price page.
    summary:
      'The user is on the prest0.ai home page — more expert capacity without more headcount: Prest0n, an AI employee ' +
      `for any team. Up to ${MAX_FILES_PER_REQUEST_UI} files ride in one request, returned as ` +
      'one deliverable. Integrations run in TWO lanes: common business apps connect ' +
      'directly; everything else Prest0n signs in to like a person, in a real browser on ' +
      'the workspace computer, with the user typing the password and the one-time code — ' +
      'that is how practice-management and court systems are reached, so never claim a ' +
      'direct connector for those.',
    contextHint:
      'Product overview, sign-up questions, pricing, or anything about getting started with prest0.',
  },

  // ── Marketing & info pages ───────────────────────────────────────────────
  '/team': {
    source: 'team',
    placeholder: 'ask a question about the team…',
    summary:
      'The user is on the Team page. It showcases Miles Maggio, founder and CEO of Prest0, ' +
      'his background in AI, proven track record creating scalable AI agents, and his vision ' +
      'for democratising personalised AI.',
    contextHint: 'Questions about the founding team, Miles\'s background, or Prest0\'s leadership.',
  },

  '/mission': {
    source: 'mission',
    placeholder: 'ask about prest0\'s mission…',
    summary:
      'The user is on the Mission page. It describes Prest0\'s mission to expand expert ' +
      'capacity for every team through AI employees that ' +
      'research, draft, coordinate, and execute so specialists can focus on work only they can do.',
    contextHint: 'Questions about Prest0\'s vision, values, multi-vertical strategy, and long-term goals.',
  },

  '/security': {
    source: 'security',
    placeholder: 'ask prest0 about enterprise security or compliance…',
    summary:
      'The user is on the Security page. Key facts: Prest0 commits to work never training a model; zero retention is being built; ' +
      'each organization gets its own workspace computer; permissions resolve per person per message; ethical walls and ' +
      'legal holds are live; outbound sends wait for a human’s approval by default. On Google Cloud (AES-256, TLS, US); ' +
      'its SOC 2 Type II and ISO 27001 are Google’s. ' +
      `${COMPLIANCE.digest} ` +
      'No prompt-redaction layer.',
    contextHint:
      'Security posture, what happens to their data, the no-training commitment, permissions and ' +
      'audit trails, which audits are ours and which are Google Cloud’s, or compliance questions.',
  },

  '/about': {
    source: 'about',
    placeholder: 'ask about prest0…',
    summary:
      'The user is on the About page, which gives a high-level overview of Prest0 as a company ' +
      'and its approach to AI-powered business automation.',
    contextHint: 'General questions about what Prest0 is and does.',
  },

  '/pricing': {
    source: 'pricing',
    placeholder: 'ask about pricing or plans…',
    // Per-employee pricing (docs/PLAN-per-employee-pricing-20261030.md): every figure interpolated from its home, and the
    // whole summary held under the digest's cap with /start still in it (scripts/generate-guest-site-digest.mjs).
    // The founding offer is said by its rule, never by whether it is open today: this is a static surface.
    summary:
      // The headline first, as every pricing surface says it (R13a-R13c): the price, unlimited seats, what a
      // full-time hire costs all overhead in, and the saving a year on each one. No hours of work (R13d).
      // (the digest generator evaluates this literal alone, so each rung is read in place: the primary offer's, then the last)
      `The user is on the Pricing page. Headline: $${(WU_PRICING.annualRungs.find((r) => PRIMARY_OFFER_SEATS >= r.minSeats && PRIMARY_OFFER_SEATS <= r.maxSeats) || WU_PRICING.annualRungs[0]).monthlyUsd.toLocaleString('en-US')} a month for a full-time employee ` +
      `(each, ${PRIMARY_OFFER_SEATS} on the annual plan, billed monthly), with unlimited seats; a full-time hire costs $${BURDENED_HIRE.monthlyUsd.toLocaleString('en-US')} a month, all overhead in: ` +
      `$${(BURDENED_HIRE.annualUsd - 12 * (WU_PRICING.annualRungs.find((r) => PRIMARY_OFFER_SEATS >= r.minSeats && PRIMARY_OFFER_SEATS <= r.maxSeats) || WU_PRICING.annualRungs[0]).monthlyUsd).toLocaleString('en-US')} saved a year on each one, modeled. ` +
      `A Prest0n employee is ${MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} PRO Work Units a month. ` +
      `Annual, billed monthly: $${WU_PRICING.annualRungs[0].monthlyUsd} for ${WU_PRICING.annualRungs[0].minSeats}–${WU_PRICING.annualRungs[0].maxSeats} down to $${WU_PRICING.annualRungs[WU_PRICING.annualRungs.length - 1].monthlyUsd} for ` +
      `${WU_PRICING.annualRungs[WU_PRICING.annualRungs.length - 1].minSeats}–${WU_PRICING.annualRungs[WU_PRICING.annualRungs.length - 1].maxSeats}; ` +
      `monthly $${WU_PRICING.monthlyRungs[0].monthlyUsd} down to $${WU_PRICING.monthlyRungs[WU_PRICING.monthlyRungs.length - 1].monthlyUsd}; ${WU_PRICING.selfServeMaxSeats + 1}+ book a meeting. ` +
      `Prepay: up to ${Math.max(...WU_PRICING.annualRungs.map((r) => Math.round((1 - r.prepayMonthlyUsd / r.monthlyUsd) * 100)))}% off. The price card is locked while you stay; a founding subscription's units grow. ` +
      `Standard Rate $${STANDARD_RATE_USD_PER_WU} per unit. ` +
      `A new organization's owner gets one ${INTERNSHIP_WORDS.name} (${INTERNSHIP_WORDS.figures.wu}, ${INTERNSHIP_WORDS.figures.length}): ` +
      '$0 today, a card starts it, and the chosen plan follows unless turned off.',
    contextHint:
      'Questions about the published rates per Prest0n employee, Work Units and the work rate card, the ladder by team ' +
      'size, the prepay discount, the price card and the founding offer, the monthly allocation and the wallet, ' +
      'auto top-up, the free internship and its card, invitation codes, or booking a demo.',
  },

  '/start': {
    source: 'start_trial',
    placeholder: 'ask about getting started…',
    summary:
      'The user is on the start page, where an invitation code starts an extended internship.',
    contextHint: 'Questions about invitation codes, the free internship, what\'s included, or how to get started.',
  },

  '/trial': {
    source: 'start_trial',
    placeholder: 'ask about getting started…',
    summary:
      'The user is on the start page, where an invitation code starts an extended internship.',
    contextHint: 'Questions about invitation codes, the free internship, what\'s included, or how to get started.',
  },

  // ── Insights / Blog ──────────────────────────────────────────────────────
  // Every address under /insights resolves here (resolvePageContext): the index, one post at /insights/<post>, and
  // the category, tag and author listings. The turn's pathname names which, so the summary says how to read it.
  '/insights': {
    source: 'insights',
    placeholder: 'ask about AI trends, articles, or prest0…',
    summary:
      'The user is on the Insights page, prest0\'s blog (prest0.ai/insights): articles on AI agents, market ' +
      'trends, industry disruption and how firms work with Prest0n, searchable and browsable by category, tag and ' +
      'author. The address says what is open: /insights lists every post, /insights/<post> is one article, and ' +
      '/insights/category/<name>, /insights/tag/<name> and /insights/author/<name> list the posts filed there.',
    contextHint:
      'Questions about the article in the page\'s address, finding articles on a topic, AI trends, or prest0.',
  },

  // ── The public roadmap and changelog (docs/PLAN-roadmap-changelog-20261020.md, R5) ─────────────────────────────
  // Every address under /roadmap and /changelog resolves to its page (resolvePageContext). Born in the new design. What's
  // new (/changelog) is live since 2026-10-21 and in the signed-out Prest0n's site digest; the roadmap joins it when R6
  // puts it on the site's live list.
  '/roadmap': {
    source: 'roadmap',
    placeholder: 'ask Prest0n what is coming next…',
    summary:
      'The user is on prest0\'s public roadmap (prest0.ai/roadmap): what the team is building now, what comes next and ' +
      'what it is exploring, in priority order, then what shipped and which plans changed in the last two months. ' +
      'Dates are targets, not promises. The address says what is open: /roadmap is the whole roadmap and ' +
      '/roadmap/<item> one item, with its stages so far and the updates about it.',
    contextHint:
      'Questions about what prest0 is building, when something is expected, why a plan changed, or what shipped.',
  },
  '/changelog': {
    source: 'changelog',
    placeholder: 'ask Prest0n what is new…',
    summary:
      'The user is on prest0\'s changelog, what\'s new (prest0.ai/changelog): every update to prest0, month by month, ' +
      'each month a version (2026.10) with its highlights and its updates, new, improved, fixed and security. The ' +
      'address says what is open: /changelog lists every version, /changelog/<update> is one update, and ' +
      '/changelog/month/<YYYY-MM> one month\'s version.',
    contextHint:
      'Questions about what changed in prest0 and when, what an update means for the user, or how to use something new.',
  },

  // ── The insights studio (Prest0 Inc. only; docs/PLAN-insights-studio-20261016.md) ──────────────────────────────
  // Signed in only, so it is never in the signed-out Prest0n's site digest (scripts/generate-guest-site-digest.mjs
  // exports its PUBLIC_ROUTES only). Every address under /admin/insights resolves here.
  '/admin/insights': {
    source: 'insights_studio',
    placeholder: 'ask Prest0n about a post…',
    summary:
      'The user is in the insights studio (prest0.ai/admin/insights), Prest0 Inc.\'s page for writing, editing and ' +
      'publishing the posts on prest0.ai/insights. The page\'s address says which view is open: /admin/insights is ' +
      'the list of posts, /admin/insights/new a new draft, /admin/insights/<post id> one post\'s editor (its ' +
      'preview at /preview), and /media, /authors, /categories and /settings the media library, the authors, the ' +
      'categories and their own publishing settings.',
    contextHint:
      'Questions about drafting, editing or publishing a post, its checklist, search and social fields, sources and ' +
      'media, or finding a post.',
  },

  // ── The roadmap studio (Prest0 Inc. only; docs/PLAN-roadmap-changelog-20261020.md) ─────────────────────────────
  // Signed in only, so it is never in the signed-out Prest0n's site digest. Every address under /admin/roadmap
  // resolves here.
  '/admin/roadmap': {
    source: 'roadmap_studio',
    placeholder: 'ask Prest0n about the roadmap…',
    summary:
      'The user is in the roadmap studio (prest0.ai/admin/roadmap), Prest0 Inc.\'s page for the master roadmap and the ' +
      'changelog. The page\'s address says which view is open: /admin/roadmap is the board of roadmap items, ' +
      '/admin/roadmap/items/<rm-id> one item\'s editor, /admin/roadmap/changelog the changelog desk, ' +
      '/admin/roadmap/changelog/<cl-id> one update\'s editor, /admin/roadmap/months/<YYYY-MM> one month\'s highlights, ' +
      'and /shipped, /expectations and /settings what shipped, the public items worth a look, and their own settings.',
    contextHint:
      'Questions about roadmap items, their stages and targets, changelog updates and monthly versions, what shipped, ' +
      'or what is public on prest0.ai/roadmap and /changelog.',
  },

  // ── The engineering page (Prest0 Inc. only; docs/PLAN-engineering-page-20261023.md) ──────────────────────────
  // Signed in only, so it is never in the signed-out Prest0n's site digest. Every address under
  // /activity/engineering resolves here.
  '/activity/engineering': {
    source: 'engineering',
    placeholder: 'ask Prest0n about a triage item or the queue…',
    summary:
      'The user is on Prest0 Inc.\'s engineering page (prest0.ai/activity/engineering). The page\'s address says which ' +
      'tab is open: /activity/engineering is development — the automatic development switch (off by default: each ' +
      'triage item gets a detailed report and a Claude Code prompt instead) and the Claude Code queue, every open triage ' +
      'item in the prioritizer\'s order with its priority score, how it scored on each criterion, its report, its prompt ' +
      'and who is on it; /integrations is integration readiness and the apps in use; /issue-updates who each reported ' +
      'issue affected and what they were told; /scheduled-jobs every job that runs on a clock; /spend Prest0\'s own ' +
      'model spend each day.',
    contextHint:
      'Questions about a triage item, its report or Claude Code prompt, why it ranked where it did, what to take next, ' +
      'an app\'s readiness, an issue\'s updates, a scheduled job, or the platform\'s own spend.',
  },

  // ── The Sales Funnel (Prest0 Inc.'s owners and admins only; docs/PLAN-sales-funnel-20261103.md) ──────────────
  // Signed in only, so it is never in the signed-out Prest0n's site digest. Every address under
  // /activity/sales-funnel resolves here.
  '/activity/sales-funnel': {
    source: 'sales_funnel',
    placeholder: 'ask Prest0n about sign-ups, trials or conversions…',
    summary:
      'The user is on Prest0 Inc.\'s Sales Funnel (prest0.ai/activity/sales-funnel), seen only by Prest0 Inc.\'s owners and ' +
      'admins: new people and organizations across the whole platform, the exact trial each is on and the stage each is ' +
      'in. The page\'s address says which tab is open: /activity/sales-funnel is the overview — monthly recurring revenue, ' +
      'the window\'s figures with their change, the people and organization funnels, new people and organizations by ' +
      'period, paying organizations, MRR and active trials over time, and what needs attention; /pipeline is who is in ' +
      'what stage now; /accounts every person or organization in a table; /cohorts cohorts by week, month or day; ' +
      '/breakdowns one dimension at a time; /guests guests before sign-up and the waitlist.',
    contextHint:
      'Questions about sign-ups, trials and their kinds, stages, conversion, MRR, cohorts, where sign-ups came from, ' +
      'guests or the waitlist.',
  },

  // ── Legal pages ──────────────────────────────────────────────────────────
  '/privacy-policy': {
    source: 'privacy_policy',
    placeholder: 'ask prest0 about privacy or data handling…',
    summary:
      'The user is on the Privacy Policy page. It covers how Prest0 collects, uses, ' +
      'and protects user data, and retention periods.',
    contextHint: 'Questions about data privacy, retention, GDPR, or data-processing agreements.',
  },

  '/cookie-policy': {
    source: 'cookie_policy',
    placeholder: 'ask about cookie preferences or consent…',
    summary:
      'The user is on the Cookie Policy page explaining which cookies prest0 uses, ' +
      'why, and how to manage preferences.',
    contextHint: 'Questions about cookies, tracking, or consent management.',
  },

  '/terms-of-service': {
    source: 'terms_of_service',
    placeholder: 'ask about terms or acceptable use…',
    summary:
      'The user is on the Terms of Service page covering acceptable use, ' +
      'account rules, IP ownership, and dispute resolution for prest0.',
    contextHint: 'Questions about terms, acceptable use, account rules, or legal obligations.',
  },

  '/legal': {
    source: 'legal',
    placeholder: 'ask about prest0\'s legal policies…',
    summary:
      'The user is on the Legal overview page which links to privacy policy, ' +
      'terms of service, cookie policy, and the Trust Center.',
    contextHint: 'General legal or compliance questions.',
  },

  // Added 2026-09-11 with the Trust Center. A visitor reading this page is
  // usually doing vendor diligence, so the guest primary should be able to
  // answer from the published subprocessor list rather than deflect.
  '/trust': {
    source: 'legal',
    placeholder: 'ask who processes your data…',
    summary:
      'The user is on the Trust Center, which publishes the subprocessor ' +
      'list — the companies that receive customer data, what each one ' +
      'gets, where it is held, and whether it is reached directly or through ' +
      'the model gateway, and names any vendor the code reaches that the list does not include yet — ' +
      'plus the security posture and how to ask for a deletion or an export. ' +
      'The data processing agreement is being re-issued (planned by 2026-10-31).',
    contextHint:
      'Security, privacy and vendor-diligence questions. Answer from the ' +
      'published list; never guess at a vendor or a region.',
  },

  // ── Product pages (authenticated) ───────────────────────────────────────
  // All authenticated account-Prest0n surfaces opt into the thread switcher
  // so users can separate parallel projects (a matter, a task, a client
  // workstream). Marketing / legal / unauth pages stay single-thread by
  // default. Internal Prest0n instances (citadel, prest0-vm, infrastructure)
  // remain force-disabled via the AND-gate in GlobalChatSystem.
  '/desktop': {
    source: 'desktop',
    placeholder: 'ask a question or start a task…',
    summary:
      'The user is on the Desktop (AgentActivity) page — their main AI workspace. ' +
      'They can run AI agents, view activity logs, and manage ongoing tasks.',
    contextHint:
      'Questions about running agents, task status, AI capabilities, or workspace management.',
    enableThreadSwitcher: true,
  },

  '/agent-activity': {
    source: 'desktop',
    placeholder: 'ask a question or start a task…',
    summary:
      'The user is on the AgentActivity page — their main AI workspace. ' +
      'They can run AI agents, view activity logs, and manage ongoing tasks.',
    contextHint:
      'Questions about running agents, task status, AI capabilities, or workspace management.',
    enableThreadSwitcher: true,
  },

  '/wallet': {
    source: 'wallet',
    placeholder: 'ask about your credits or billing…',
    summary:
      'The user is on the Wallet page showing their prest0 credit balance, ' +
      'transaction history, and top-up options.',
    contextHint: 'Questions about credits, billing, transactions, or topping up.',
    enableThreadSwitcher: true,
  },

  '/activity': {
    source: 'activity',
    placeholder: 'ask about usage or activity…',
    summary:
      'The user is on the Activity page showing usage analytics: tokens used, spend, ' +
      'per-model and per-member breakdowns, usage limits, and a detailed request history.',
    contextHint:
      'Questions about usage, token consumption, spend, usage limits, or which members ' +
      'are using what.',
    enableThreadSwitcher: true,
  },

  '/settings': {
    source: 'settings',
    placeholder: 'ask about settings or configuration…',
    summary:
      'The user is on the Settings page where they can configure their prest0 account, ' +
      'API connections, preferences, and integrations.',
    contextHint: 'Questions about account settings, API keys, configuration, or integrations.',
    enableThreadSwitcher: true,
  },

  '/profile': {
    source: 'profile',
    placeholder: 'ask about your profile or account…',
    summary:
      'The user is on the Profile page where they can update personal details, ' +
      'avatar, and account preferences.',
    contextHint: 'Questions about profile management or account details.',
    enableThreadSwitcher: true,
  },

  '/notifications': {
    source: 'notifications',
    placeholder: 'ask about notifications…',
    summary:
      'The user is on the Notifications page showing activity alerts and system messages.',
    contextHint: 'Questions about notifications, alerts, or activity history.',
    enableThreadSwitcher: true,
  },

  '/integrations': {
    source: 'integrations',
    placeholder: 'ask about integrations or connections…',
    summary:
      'The user is on the Integrations page where they can connect external services ' +
      'and tools to their prest0 account.',
    contextHint: 'Questions about connecting external tools, APIs, or third-party integrations.',
    enableThreadSwitcher: true,
  },

  '/subscription': {
    source: 'subscription',
    placeholder: 'ask about your subscription or plan…',
    summary:
      'The user is on the Subscription management page where they can upgrade, ' +
      'downgrade, or manage their prest0 plan.',
    contextHint: 'Questions about subscription plans, upgrades, or billing cycles.',
    enableThreadSwitcher: true,
  },

  '/subscription/agreement': {
    source: 'subscription_agreement',
    placeholder: 'ask about your agreement or your plan…',
    summary:
      'The user is on the service agreement page, reviewing and signing the personal ' +
      'agreement for a hire or a plan change (plan, details, review, sign, pay).',
    contextHint: 'Questions about the agreement\'s terms, what is charged and when, renewal, or cancelling.',
    enableThreadSwitcher: true,
  },

  '/infrastructure': {
    source: 'infrastructure',
    placeholder: 'ask about infrastructure or VM management…',
    summary:
      'The user is on the Infrastructure page for managing compute resources and VM instances.',
    contextHint: 'Questions about VMs, compute resources, or infrastructure management.',
  },



  // The partner firm's program page (/justiceisurright) is retired (Miles, 2026-10-24): its address forwards to
  // /desktop, so it has no page context of its own; the firm's languages and practice areas are account facts.

  // ── Team invitation landing ──────────────────────────────────────────────
  // Owner directive 2026-08-12: the invitee's very first touch with prest0 is
  // this page, often before they have an account. The chat must KNOW it —
  // its first message offers to answer questions about the invitation, and
  // the starter chips are invitation questions, not marketing prompts.
  '/accept-invite': {
    source: 'accept_invite',
    placeholder: 'ask about your invitation…',
    summary:
      'The user is on the team-invitation acceptance page. Someone at a company that uses ' +
      'Prest0 invited them (by email) to join that company\'s workspace. From here they either ' +
      'create a prest0 account (or sign in) and the invitation is accepted automatically, or — ' +
      'if already signed in — they confirm joining with the current account or switch accounts. ' +
      'Facts that matter: invitations expire 7 days after being sent; the invite link can be ' +
      'accepted even when the signed-in account uses a different email than the one the invite ' +
      'was sent to (the membership records the originally-invited address); joining gives them ' +
      'a role in the inviter\'s workspace with the permissions that role grants; their personal ' +
      'data and any personal prest0 account stay separate from the team workspace. Prest0 is an ' +
      'AI-employee platform — the team they are joining uses it to run AI agents for research, ' +
      'drafting, and day-to-day work.',
    contextHint:
      'Questions about this invitation: what prest0 is, what joining means, what a role can do, ' +
      'whether to create an account or sign in, what happens to their data, or invite expiry.',
    welcomeMessage: {
      text:
        'welcome! you\'re looking at a team invitation — I can answer any questions about it: ' +
        'what prest0 is, what happens when you accept, or what you\'ll be able to do on the team.',
    },
    welcomeStarters: [
      'what happens when I accept this invitation?',
      'what is prest0?',
      'what will I be able to see and do on the team?',
      'can I join with a different email than the one invited?',
    ],
  },

  // NOTE (Round M, 2026-07-15): the '/citadel' web page (and its registry
  // entry) was deleted entirely per owner directive. The route now falls to
  // the app's catch-all 404 and resolves DEFAULT_PAGE_CONTEXT like any other
  // unknown route. Internal 'citadel' SOURCE/instance-id naming (the internal
  // bot VM surfaces guarded by isInternalPrest0nSurface) is unrelated to the
  // deleted web page and stays.
};

/**
 * Routes where the GlobalChatSystem should NOT render at all.
 * These are flows where a floating chat would be confusing or intrusive.
 */
export const CHAT_EXCLUDED_ROUTES = new Set([
  '/auth',
  '/auth/callback',
  '/onboarding',
  // The finish-setup screen (sign-in requirements, 2026-10-29): a session that must finish setting up its sign-in has
  // no chat (it is refused everywhere but the setup doors), and the screen is only that.
  '/sign-in-setup',
]);

/**
 * Resolve page context for the given pathname.
 * Handles exact matches, the /insights/*, /roadmap/*, /changelog/*, /admin/insights/*, /admin/roadmap/*,
 * /activity/engineering/* and /activity/sales-funnel/* prefixes, and
 * falls back to DEFAULT_PAGE_CONTEXT.
 */
export function resolvePageContext(pathname: string): PageChatContext {
  // Exact match
  if (PAGE_CONTEXT_REGISTRY[pathname]) {
    return PAGE_CONTEXT_REGISTRY[pathname];
  }

  // Prefix match for blog/insights sub-routes
  if (pathname.startsWith('/insights/')) {
    return PAGE_CONTEXT_REGISTRY['/insights'];
  }

  // The public roadmap's and changelog's pages (an item, an update, a version)
  if (pathname.startsWith('/roadmap/')) {
    return PAGE_CONTEXT_REGISTRY['/roadmap'];
  }
  if (pathname.startsWith('/changelog/')) {
    return PAGE_CONTEXT_REGISTRY['/changelog'];
  }

  // The insights studio's views (a post's editor, the media library, …)
  if (pathname.startsWith('/admin/insights/')) {
    return PAGE_CONTEXT_REGISTRY['/admin/insights'];
  }

  // The roadmap studio's views (an item's editor, the changelog desk, …)
  if (pathname.startsWith('/admin/roadmap/')) {
    return PAGE_CONTEXT_REGISTRY['/admin/roadmap'];
  }

  // The engineering page's tabs (integrations, issue updates, scheduled jobs, spend)
  if (pathname.startsWith('/activity/engineering/')) {
    return PAGE_CONTEXT_REGISTRY['/activity/engineering'];
  }

  // The Sales Funnel's tabs (pipeline, accounts, cohorts, breakdowns, guests & leads)
  if (pathname.startsWith('/activity/sales-funnel/')) {
    return PAGE_CONTEXT_REGISTRY['/activity/sales-funnel'];
  }

  // Prefix match for integrations callback
  if (pathname.startsWith('/integrations/')) {
    return PAGE_CONTEXT_REGISTRY['/integrations'];
  }

  return DEFAULT_PAGE_CONTEXT;
}
